import { clsx } from 'clsx'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2Icon,
  InfoIcon,
} from 'lucide-react'

export const Logo = ({ url, size = 24 }: { url: string; size?: number }) => (
  <div className="ds-border-b ds-border-gray-200">
    <div className="flex items-center gap-2">
      <div
        className={`w-[${size + 4}px] h-[${size + 4}px] ds-border-half ds-border-white-200 ds-rounded-6 ds-flex ds-items-center ds-justify-center`}
      >
        <img
          src={url}
          alt="logo"
          className={`w-[${size}px] h-[${size}px] select-none`}
        />
      </div>
    </div>
  </div>
)

export const Badge = ({
  children,
  state = 'success',
  className,
}: {
  children: React.ReactNode
  state?: 'success' | 'error' | 'warning' | 'info'
  className?: string
}) => {
  const stateStyles = {
    success: {
      bgColor: 'ds-bg-green-50/15',
      textColor: 'ds-text-green-900',
      borderColor: 'ds-border-green-900',
    },
    error: {
      bgColor: 'ds-bg-red-50/15',
      textColor: 'ds-text-red-900',
      borderColor: 'ds-border-red-900',
    },
    warning: {
      bgColor: 'ds-bg-yellow-50/15',
      textColor: 'ds-text-yellow-900',
      borderColor: 'ds-border-yellow-900',
    },
    info: {
      bgColor: 'ds-bg-blue-50/15',
      textColor: 'ds-text-blue-900',
      borderColor: 'ds-border-blue-900',
    },
  }
  const { bgColor, textColor, borderColor } = stateStyles[state]
  return (
    <div
      className={clsx(
        'ds-border-half ds-gap-4 !ds-text-12 ds-font-400 ds-border-green-900 font-400 font-text ds-bg-green-50/15 ds-text-green-900 text-14 flex h-[28px] items-center justify-center rounded-[200px] px-2 py-[4px] pb-[3px] leading-none',
        bgColor,
        textColor,
        borderColor,
        className,
      )}
    >
      {children}

      {state === 'success' && <CheckCircle2Icon size={14} strokeWidth={2} />}
      {state === 'error' && <AlertTriangle size={14} strokeWidth={2} />}
      {state === 'warning' && <AlertCircle size={14} strokeWidth={2} />}
      {state === 'info' && <InfoIcon size={14} strokeWidth={2} />}
    </div>
  )
}

export const Tag = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => (
  <div
    className={clsx(
      'ds-text-12 ds-font-400 ds-text-gray-600 ds-border-half ds-border-white-400 ds-px-8 ds-py-1 ds-rounded-12',
      className,
    )}
  >
    {children}
  </div>
)

export const InfoButton = ({
  state,
  icon,
}: {
  state: 'success' | 'error' | 'warning' | 'info'
  icon?: React.ReactNode
}) => {
  const stateStyles = {
    success: { bgColor: 'ds-bg-green-50/20', textColor: 'ds-text-green-900' },
    error: { bgColor: 'ds-bg-red-50/20', textColor: 'ds-text-red-900' },
    warning: { bgColor: 'ds-bg-orange-50/20', textColor: 'ds-text-orange-900' },
    info: { bgColor: 'ds-bg-blue-50/20', textColor: 'ds-text-blue-900' },
  }
  const { bgColor, textColor } = stateStyles[state]
  const Icon = icon || <AlertTriangle height={12} width={12} strokeWidth={2} />
  return (
    <div
      className={clsx(
        'flex items-center justify-center rounded-[4px] p-[4px]',
        bgColor,
        textColor,
      )}
    >
      {Icon}
    </div>
  )
}

export const blockHeadingStyle =
  'ds-text-14 ds-font-500 ds-text-black-800 leading-none pr-4'
export const blockSubHeadingStyle =
  'ds-text-12 ds-font-400 ds-font-mono ds-text-black-900'

export const Dot = ({
  size = 14,
  status = 'info',
}: {
  size?: number
  status?: 'info' | 'success' | 'error' | 'warning' | 'plain'
}) => {
  const statusStyles = {
    info: { bgColor: 'ds-bg-blue-50', borderColor: 'ds-border-blue-600' },
    success: { bgColor: 'ds-bg-green-50', borderColor: 'ds-border-green-800' },
    error: { bgColor: 'ds-bg-red-50', borderColor: 'ds-border-red-800' },
    warning: {
      bgColor: 'ds-bg-orange-50',
      borderColor: 'ds-border-orange-800',
    },
    plain: { bgColor: 'ds-bg-white', borderColor: 'ds-border-white-200' },
  }
  const { bgColor, borderColor } = statusStyles[status]
  return (
    <div
      className={`absolute left-[-22px] top-[2px] z-50 w-[${size}px] h-[${size}px] rounded-[20px] border-[1.5px] ${borderColor} ${bgColor} ds-rounded-full ds-flex ds-items-center ds-justify-center`}
    ></div>
  )
}

export const LineHeading = () => (
  <div className="absolute left-[-20px] top-[8px] min-h-[1px] w-[40px] max-w-[21px] bg-gray-200"></div>
)
